import React, { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import {
  User, Bot, TrendingUp, TrendingDown, X, Search, Check, Infinity as InfinityIcon,
  BarChart3, Bitcoin, Globe, Cpu, ShieldCheck, Smartphone, Headphones, Loader2, Circle, ChevronDown,
  Wallet, ArrowRightLeft, Share2, Trophy, ArrowUpFromLine, Landmark, Copy,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import TradeShareCard from "@/components/TradeShareCard";
import RankShareCard from "@/components/RankShareCard";

const CATS = ["FOREX", "CRYPTO", "METALS", "INDICES"];
const CAT_MAP = { FOREX: "forex", CRYPTO: "crypto", METALS: "metal", INDICES: "index" };
const SELF_TF = ["15s", "30s", "1m", "5m", "10m", "15m", "1H", "1D"];
const BOT_TF = ["1m", "5m", "15m", "1H", "4H", "1D"];
const AMTS = [10, 20, 50, 100];
const fmt = (n, d = 2) => (n === null || n === undefined ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const dg = (s) => (s?.category === "forex" ? 4 : 2);

function StatusDot({ status }) {
  const m = { LIVE: "text-emerald-400", STALE: "text-amber-400", RECONNECTING: "text-amber-400", DISCONNECTED: "text-rose-500" };
  return <Circle size={7} className={cn("fill-current", m[status] || "text-rose-500")} />;
}

const neon = { border: "1px solid rgba(16,185,129,.45)", boxShadow: "0 0 0 1px rgba(16,185,129,.12), 0 0 26px rgba(16,185,129,.12)", background: "linear-gradient(180deg,#06120c,#030906)" };

function Candles({ data }) {
  if (!data?.length) return <div className="grid h-full place-items-center text-xs text-emerald-200/40">Chart feed unavailable</div>;
  const w = 560, h = 210, pad = 6;
  const lo = Math.min(...data.map((c) => c.l)), hi = Math.max(...data.map((c) => c.h));
  const sx = (i) => pad + (i * (w - pad * 2)) / data.length;
  const sy = (v) => pad + (h - pad * 2) * (1 - (v - lo) / (hi - lo || 1));
  const cw = Math.max(2, (w - pad * 2) / data.length - 2);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-full w-full" preserveAspectRatio="none">
      {data.map((c, i) => {
        const up = c.c >= c.o, col = up ? "#34d399" : "#f43f5e", x = sx(i) + cw / 2;
        return (<g key={i}>
          <line x1={x} x2={x} y1={sy(c.h)} y2={sy(c.l)} stroke={col} strokeWidth="1" />
          <rect x={sx(i)} width={cw} y={sy(Math.max(c.o, c.c))} height={Math.max(1, Math.abs(sy(c.o) - sy(c.c)))} fill={col} />
        </g>);
      })}
    </svg>
  );
}

function TF({ list, val, set, testprefix }) {
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((t) => (
        <button key={t} onClick={() => set(t)} data-testid={`${testprefix}-tf-${t}`}
          className={cn("rounded px-2 py-0.5 text-[10px] font-bold transition-colors", val === t ? "bg-emerald-500 text-black" : "bg-white/5 text-emerald-200/60 hover:text-white")}>{t}</button>
      ))}
    </div>
  );
}

function ChartHead({ sym, q, hi, lo, flash }) {
  return (
    <div className="mb-2">
      <div className="flex items-center gap-2">
        <span className="grid h-6 w-6 place-items-center rounded-full bg-amber-400/20 text-amber-400"><Bitcoin size={14} /></span>
        <span className="text-base font-extrabold text-white">{sym}</span>
        <span className="flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-400"><StatusDot status={q?.status} /> {q?.status === "LIVE" ? "LIVE" : q?.status || "—"}</span>
        <span className={cn("ml-auto rounded px-1 text-lg font-extrabold text-white", flash === "BUY" && "flash-up", flash === "SELL" && "flash-down")}>{fmt(q?.last, 2)}</span>
        {q?.change !== null && q?.change !== undefined && (
          <span className={cn("text-sm font-bold", q.change >= 0 ? "text-emerald-400" : "text-rose-400")}>{q.change >= 0 ? "+" : ""}{q.change}%</span>
        )}
      </div>
      <div className="mt-1 flex gap-6 text-[10px] text-emerald-200/50">
        <span>24h High <b className="text-emerald-100">{fmt(hi, 2)}</b></span>
        <span>24h Low <b className="text-emerald-100">{fmt(lo, 2)}</b></span>
        {q?.bid && <span>Bid/Ask <b className="text-emerald-100">{fmt(q.bid, 2)}/{fmt(q.ask, 2)}</b></span>}
      </div>
    </div>
  );
}

export default function Trading() {
  const [market, setMarket] = useState([]);
  const [cat, setCat] = useState("CRYPTO");
  const [search, setSearch] = useState("");
  const [selfSym, setSelfSym] = useState("BTC/USDT");
  const [botSym, setBotSym] = useState("XAU/USD");
  const [selfTf, setSelfTf] = useState("1m");
  const [botTf, setBotTf] = useState("15m");
  const [selfCandles, setSelfCandles] = useState({ candles: [], available: true });
  const [botCandles, setBotCandles] = useState({ candles: [], available: true });
  const [acct, setAcct] = useState(null);
  const [botAcct, setBotAcct] = useState(null);
  const [positions, setPositions] = useState([]);
  const [botPositions, setBotPositions] = useState([]);
  const [trades, setTrades] = useState([]);
  const [botTrades, setBotTrades] = useState([]);
  const [side, setSide] = useState("BUY");
  const [amount, setAmount] = useState(20);
  const [selfTab, setSelfTab] = useState("open");
  const [botTab, setBotTab] = useState("active");
  const [strategy, setStrategy] = useState("ALEXA Pro (Balanced)");
  const [strategies, setStrategies] = useState(["ALEXA Pro (Balanced)"]);
  const [alloc, setAlloc] = useState(500);
  const [placing, setPlacing] = useState(false);
  const [botBusy, setBotBusy] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [wAmt, setWAmt] = useState(50);
  const [selfFlash, setSelfFlash] = useState(null);
  const [shareTrade, setShareTrade] = useState(null);
  const [fundOpen, setFundOpen] = useState(false);
  const [fundTab, setFundTab] = useState("deposit");
  const [wSource, setWSource] = useState("capital");
  const [leaders, setLeaders] = useState(null);
  const [rankShare, setRankShare] = useState(null);
  const { user } = useAuth();

  const q = useMemo(() => Object.fromEntries(market.map((m) => [m.symbol, m])), [market]);
  const selfQ = q[selfSym]; const botQ = q[botSym];
  const list = useMemo(() => market.filter((m) => m.category === CAT_MAP[cat] && m.symbol.toLowerCase().includes(search.toLowerCase())), [market, cat, search]);

  const loadMarket = () => api.get("/trading/market").then((r) => setMarket(r.data.items || [])).catch(() => {});
  const loadAcct = () => api.get("/trading/account").then((r) => setAcct(r.data)).catch(() => {});
  const loadBotAcct = () => api.get("/trading/bot/account").then((r) => { setBotAcct(r.data); if (r.data.strategies) setStrategies(r.data.strategies); }).catch(() => {});
  const loadPos = () => api.get("/trading/positions").then((r) => setPositions(r.data.positions || [])).catch(() => {});
  const loadBotPos = () => api.get("/trading/bot/positions").then((r) => setBotPositions(r.data.positions || [])).catch(() => {});
  const loadTrades = () => api.get("/trading/trades?filter=all").then((r) => setTrades(r.data.trades || [])).catch(() => {});
  const loadBotTrades = () => api.get("/trading/bot/trades").then((r) => setBotTrades(r.data.trades || [])).catch(() => {});
  const loadWallet = () => api.get("/trading/wallet").then((r) => setWallet(r.data)).catch(() => {});
  const loadLeaders = () => api.get("/trading/leaderboard").then((r) => setLeaders(r.data)).catch(() => {});

  useEffect(() => {
    const all = () => { loadMarket(); loadAcct(); loadBotAcct(); loadPos(); loadBotPos(); loadTrades(); loadBotTrades(); loadWallet(); loadLeaders(); };
    all(); const id = setInterval(() => { loadMarket(); loadAcct(); loadBotAcct(); loadPos(); loadBotPos(); }, 5000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => { api.get(`/trading/candles/${encodeURIComponent(selfSym)}?tf=${selfTf}`).then((r) => setSelfCandles(r.data)).catch(() => setSelfCandles({ candles: [], available: false })); }, [selfSym, selfTf]);
  useEffect(() => { api.get(`/trading/candles/${encodeURIComponent(botSym)}?tf=${botTf}`).then((r) => setBotCandles(r.data)).catch(() => setBotCandles({ candles: [], available: false })); }, [botSym, botTf]);

  const placeOrder = async () => {
    if (selfQ?.status !== "LIVE") { toast.error("MARKET DATA UNAVAILABLE"); return; }
    setPlacing(true);
    try {
      const r = await api.post("/trading/order", { symbol: selfSym, side, amount: Number(amount), order_type: "MARKET", client_order_id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` });
      toast.success(`${side} ${selfSym} opened @ ${r.data.entry}`);
      setSelfFlash(side); setTimeout(() => setSelfFlash(null), 700);
      loadAcct(); loadPos(); loadWallet();
    } catch (e) { toast.error(e?.response?.data?.detail || "Order failed"); } finally { setPlacing(false); }
  };
  const closePos = async (id) => { try { const r = await api.post(`/trading/positions/${id}/close`); toast[r.data.net_pnl >= 0 ? "success" : "error"](`Closed · P&L $${fmt(r.data.net_pnl)}`); loadAcct(); loadPos(); loadTrades(); loadWallet(); } catch (e) { toast.error(e?.response?.data?.detail || "Close failed"); } };
  const allocateBot = async () => { try { await api.post("/trading/bot/allocate", { amount: Number(alloc) }); toast.success(`Allocated $${fmt(Number(alloc))} to ALEXA Bot`); loadBotAcct(); } catch (e) { toast.error(e?.response?.data?.detail || "Allocate failed"); } };
  const botOpen = async () => {
    setBotBusy(true);
    try { const r = await api.post("/trading/bot/open", { strategy }); toast.success(`ALEXA Bot activated: ${r.data.side} ${r.data.symbol}`); loadBotAcct(); loadBotPos(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bot could not open a trade"); } finally { setBotBusy(false); }
  };
  const closeBot = async (id) => { try { const r = await api.post(`/trading/bot/positions/${id}/close`); toast[r.data.net_pnl >= 0 ? "success" : "error"](`Bot closed · P&L $${fmt(r.data.net_pnl)}`); loadBotAcct(); loadBotPos(); loadBotTrades(); } catch (e) { toast.error(e?.response?.data?.detail || "Close failed"); } };
  const depositWallet = async (amt = wAmt) => { try { await api.post("/trading/wallet/deposit", { amount: Number(amt) }); toast.success(`$${fmt(Number(amt))} added to Trading Wallet`); loadWallet(); loadAcct(); } catch (e) { toast.error(e?.response?.data?.detail || "Deposit failed"); } };
  const openWithdraw = (source) => { setWSource(source); setFundTab("withdraw"); setFundOpen(true); };

  const BOT_FEATURES = ["AI Market Analysis", "Auto Entry & Exit", "Dynamic Stop Loss", "Trailing Take Profit", "24/7 Monitoring", "Risk Management"];
  const FOOTER = [[BarChart3, "Real-Time", "Market Data"], [ShieldCheck, "Secure & Safe", "Trading Environment"], [Cpu, "AI Powered", "Trading Bot"], [Smartphone, "Multi-Asset", "Support"], [Globe, "Desktop & Mobile", "Access"], [Headphones, "24/7", "Support"]];

  return (
    <div className="space-y-4" data-testid="trading-terminal">
      {/* Banner */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl px-5 py-3" style={neon}>
        <div>
          <h1 className="text-lg font-extrabold tracking-wide text-white sm:text-xl">SMART TRADING</h1>
          <p className="text-lg font-extrabold text-amber-400 sm:text-xl">TWO POWERFUL WAYS</p>
        </div>
        <div className="hidden items-center gap-5 text-[10px] font-bold text-emerald-200/70 md:flex">
          <span className="flex flex-col items-center gap-1"><BarChart3 size={18} className="text-emerald-400" />FOREX</span>
          <span className="flex flex-col items-center gap-1"><Bitcoin size={18} className="text-amber-400" />CRYPTO</span>
          <span className="flex flex-col items-center gap-1"><Globe size={18} className="text-emerald-400" />GLOBAL MARKETS</span>
          <span className="flex flex-col items-center gap-1"><Cpu size={18} className="text-emerald-400" />AI BOT</span>
        </div>
        <div className="text-right">
          <p className="text-sm font-extrabold text-white">TRADE SMARTER</p>
          <p className="text-sm font-extrabold text-emerald-400">LIVE BETTER</p>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-400/5 px-4 py-2 text-[11px] text-amber-200/90" data-testid="mlm-commission-note">
        <ShieldCheck size={14} className="mt-0.5 shrink-0 text-amber-400" />
        <span>Your MLM activation stays invested with ALEXA Bot and earns a fixed daily return credited as <b>bot commission</b> (your locked ROI rate — unchanged). <b>Self-Trade</b> below is real — you trade live-priced markets from your Trading Wallet, and profits are fully yours with no cap.</span>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        {/* ============ SELF TRADE ============ */}
        <div className="neon-pulse rounded-2xl p-4" style={neon} data-testid="self-trade-panel">
          <div className="mb-3 flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-emerald-500/15 text-emerald-400"><User size={20} /></span>
            <div>
              <h2 className="text-base font-extrabold text-white">SELF TRADE</h2>
              <p className="text-[11px] text-emerald-200/60">You Decide. You Trade. Full Control.</p>
            </div>
            <button onClick={() => { setFundTab("deposit"); setFundOpen(true); }} data-testid="open-fund-wallet-btn" className="ml-auto flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-[11px] font-extrabold text-black ring-2 ring-emerald-300/70 animate-pulse shadow-[0_0_18px_rgba(16,185,129,0.55)] hover:bg-emerald-400 hover:animate-none"><Wallet size={14} /> Fund Wallet</button>
          </div>

          <div className="grid gap-3 lg:grid-cols-[190px_1fr]">
            {/* symbol list */}
            <div>
              <div className="mb-2 flex gap-1">
                {CATS.map((c) => (
                  <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c}`}
                    className={cn("flex-1 rounded px-1 py-1 text-[9px] font-bold", cat === c ? "bg-emerald-500 text-black" : "bg-white/5 text-emerald-200/60")}>{c}</button>
                ))}
              </div>
              <div className="mb-2 flex items-center gap-1 rounded-lg border border-white/10 bg-black/30 px-2">
                <Search size={12} className="text-emerald-200/40" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search symbol..." data-testid="symbol-search"
                  className="w-full bg-transparent py-1.5 text-[11px] text-white outline-none" />
              </div>
              <div className="max-h-[360px] space-y-0.5 overflow-y-auto">
                {list.length === 0 && <p className="py-6 text-center text-[10px] text-emerald-200/40">No instruments in {cat} from provider</p>}
                {list.map((m) => (
                  <button key={m.symbol} onClick={() => setSelfSym(m.symbol)} data-testid={`sym-${m.symbol.replace("/", "-")}`}
                    className={cn("flex w-full items-center justify-between rounded px-2 py-1.5 text-left hover:bg-white/5", selfSym === m.symbol && "bg-emerald-500/10 ring-1 ring-emerald-500/40")}>
                    <span className="flex items-center gap-1.5"><StatusDot status={m.status} /><span className="text-[11px] font-bold text-white">{m.symbol}</span></span>
                    <span className="text-right">
                      <span className="block font-mono text-[11px] text-white">{fmt(m.price, dg(m))}</span>
                      {m.change !== null && <span className={cn("block text-[9px]", m.change >= 0 ? "text-emerald-400" : "text-rose-400")}>{m.change >= 0 ? "+" : ""}{m.change}%</span>}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* chart + order */}
            <div>
              <ChartHead sym={selfSym} q={selfQ} hi={selfQ?.last} lo={selfQ?.last} flash={selfFlash} />
              <TF list={SELF_TF} val={selfTf} set={setSelfTf} testprefix="self" />
              <div className="my-2 h-[200px] w-full rounded-lg border border-white/5 bg-black/30 p-1"><Candles data={selfCandles.available ? selfCandles.candles : []} /></div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase text-emerald-200/50">Amount (USDT)</p>
                  <div className="mt-1 grid grid-cols-4 gap-1">
                    {AMTS.map((a) => (<button key={a} onClick={() => setAmount(a)} data-testid={`amt-${a}`}
                      className={cn("rounded py-1 text-[11px] font-bold", Number(amount) === a ? "bg-emerald-500 text-black" : "bg-white/5 text-emerald-200/60")}>${a}</button>))}
                  </div>
                  <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="amount-input"
                    className="mt-1 w-full rounded border border-white/10 bg-black/30 px-2 py-1 text-sm text-white outline-none" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase text-emerald-200/50">Potential Payout <span className="normal-case text-emerald-200/30">(Example)</span></p>
                  <p className="mt-2 text-2xl font-extrabold text-emerald-400">+80%</p>
                  <p className="text-xs text-emerald-200/60">≈ ${fmt(Number(amount) * 0.8)}</p>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <button onClick={() => { setSide("BUY"); placeOrder(); }} disabled={placing} data-testid="self-buy-btn"
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-emerald-500 py-3 text-sm font-extrabold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600 disabled:opacity-50">
                  {placing && side === "BUY" ? <Loader2 className="animate-spin" size={16} /> : <TrendingUp size={16} />} BUY
                </button>
                <button onClick={() => { setSide("SELL"); placeOrder(); }} disabled={placing} data-testid="self-sell-btn"
                  className="flex items-center justify-center gap-1.5 rounded-lg bg-rose-500 py-3 text-sm font-extrabold text-white shadow-lg shadow-rose-500/30 transition hover:bg-rose-600 disabled:opacity-50">
                  {placing && side === "SELL" ? <Loader2 className="animate-spin" size={16} /> : <TrendingDown size={16} />} SELL
                </button>
              </div>
              {selfQ?.status !== "LIVE" && <p className="mt-1 text-center text-[10px] font-bold text-rose-400">MARKET DATA UNAVAILABLE</p>}
            </div>
          </div>

          {/* self tabs */}
          <div className="mt-4">
            <div className="flex gap-4 border-b border-white/10 text-[11px] font-bold">
              {[["open", `Open Trades (${positions.length})`], ["history", "Trade History"], ["wallet", "Trading Wallet"]].map(([k, lbl]) => (
                <button key={k} onClick={() => setSelfTab(k)} data-testid={`self-tab-${k}`}
                  className={cn("pb-1.5", selfTab === k ? "border-b-2 border-emerald-500 text-emerald-400" : "text-emerald-200/50")}>{lbl}</button>
              ))}
            </div>
            <div className="mt-2 max-h-40 overflow-auto">
              {selfTab === "wallet" && wallet && (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {[["Capital", wallet.capital], ["Available", wallet.available], ["In Trade", wallet.locked], ["Self-Trade Profit", wallet.profit], ["Trading Commission", wallet.trading_commission], ["Main Fund", wallet.mlm_fund_balance]].map(([l, v]) => (
                      <div key={l} className="wallet-3d rounded-xl py-2"><p className="text-[9px] uppercase text-emerald-200/50">{l}</p><p className={cn("text-sm font-bold", v >= 0 ? "text-white" : "text-rose-400")}>${fmt(v)}</p></div>
                    ))}
                  </div>
                  {wallet.blocked && <p className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-center text-[11px] font-bold text-rose-300">Trading disabled by administrator</p>}
                  <div className="flex flex-wrap items-center gap-2 rounded-lg bg-white/5 p-2">
                    <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-emerald-200/50"><Wallet size={12} /> Fund Capital</span>
                    <input type="number" value={wAmt} onChange={(e) => setWAmt(e.target.value)} data-testid="wallet-amount"
                      className="w-24 rounded border border-white/10 bg-black/30 px-2 py-1 text-xs text-white outline-none" />
                    <button onClick={() => depositWallet()} data-testid="wallet-deposit-btn" className="flex items-center gap-1 rounded bg-emerald-500 px-3 py-1.5 text-[11px] font-bold text-black hover:bg-emerald-400"><ArrowRightLeft size={12} /> Deposit</button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => openWithdraw("capital")} data-testid="wd-open-capital" className="flex flex-col items-center gap-0.5 rounded-lg border border-emerald-500/40 py-2 text-[10px] font-bold text-emerald-300 hover:bg-emerald-500/10"><ArrowUpFromLine size={13} /> Capital<span className="text-[9px] text-emerald-200/50">${fmt(wallet.available)}</span></button>
                    <button onClick={() => openWithdraw("profit")} data-testid="wd-open-profit" className="flex flex-col items-center gap-0.5 rounded-lg bg-amber-400 py-2 text-[10px] font-extrabold text-black hover:bg-amber-300"><ArrowUpFromLine size={13} /> Profit<span className="text-[9px] font-bold text-black/70">${fmt(wallet.profit)}</span></button>
                    <button onClick={() => openWithdraw("commission")} data-testid="wd-open-commission" className="flex flex-col items-center gap-0.5 rounded-lg border border-amber-400/40 py-2 text-[10px] font-bold text-amber-300 hover:bg-amber-400/10"><Landmark size={13} /> Team Income<span className="text-[9px] text-amber-200/50">${fmt(wallet.trading_commission)}</span></button>
                  </div>
                  <p className="text-[10px] text-emerald-200/40">Three separate self-trade wallets — each withdraws directly to your BEP20 USDT address (min ${fmt(5)}). <b>Capital</b> = your deposit. <b>Profit</b> = self-trade earnings (28% shared to your 10-level team, you get 72%). <b>Team Income</b> = 10-level commission your team's trading earns you.</p>
                </div>
              )}
              {selfTab === "wallet" && !wallet && <Empty text="Loading wallet…" />}
              {selfTab === "open" && (positions.length ? <PosTable rows={positions} onClose={closePos} tp="self" /> : <Empty text="No open trades" />)}
              {selfTab === "history" && (trades.length ? <TradeTable rows={trades} onShare={setShareTrade} /> : <Empty text="No closed trades" />)}
            </div>
          </div>
        </div>

        {/* ============ ALEXA BOT TRADE ============ */}
        <div className="neon-pulse rounded-2xl p-4" style={neon} data-testid="bot-trade-panel">
          <div className="mb-3 flex items-center gap-3">
            <span className={cn("grid h-10 w-10 place-items-center rounded-full transition", botPositions.length > 0 ? "animate-pulse bg-white text-emerald-700" : "bg-emerald-500/15 text-emerald-400")}><Bot size={20} /></span>
            <div>
              <h2 className="text-base font-extrabold text-white">ALEXA BOT TRADE</h2>
              <p className="text-[11px] text-emerald-200/60">Our AI Trades For You. No Time Limit.</p>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-[1fr_200px]">
            <div>
              <ChartHead sym={botSym} q={botQ} hi={botQ?.last} lo={botQ?.last} />
              <TF list={BOT_TF} val={botTf} set={setBotTf} testprefix="bot" />
              <div className="relative my-2 h-[200px] w-full rounded-lg border border-white/5 bg-black/30 p-1">
                <Candles data={botCandles.available ? botCandles.candles : []} />
                {botPositions.length > 0 && (<div className="bot-runner" data-testid="bot-runner"><Bot size={24} /></div>)}
              </div>
              <div className="flex gap-1 overflow-x-auto">
                {market.filter((m) => m.chart).map((m) => (
                  <button key={m.symbol} onClick={() => setBotSym(m.symbol)}
                    className={cn("shrink-0 rounded px-2 py-1 text-[10px] font-bold", botSym === m.symbol ? "bg-emerald-500/20 text-emerald-300" : "bg-white/5 text-emerald-200/50")}>{m.symbol}</button>
                ))}
              </div>
            </div>

            {/* strategy + features */}
            <div>
              <p className="text-[10px] font-bold uppercase text-emerald-200/50">Bot Strategy</p>
              <div className="relative mt-1">
                <select value={strategy} onChange={(e) => setStrategy(e.target.value)} data-testid="bot-strategy"
                  className="w-full appearance-none rounded-lg border border-emerald-500/40 bg-black/40 px-2 py-2 text-[11px] font-semibold text-white outline-none">
                  {strategies.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <ChevronDown size={14} className="pointer-events-none absolute right-2 top-2.5 text-emerald-300" />
              </div>
              <div className="mt-2 space-y-1.5">
                {BOT_FEATURES.map((f) => (<p key={f} className="flex items-center gap-1.5 text-[11px] text-emerald-100"><Check size={13} className="text-emerald-400" />{f}</p>))}
              </div>
              {botAcct && (
                <div className="mt-2 grid grid-cols-2 gap-2 text-center">
                  <div className="rounded-lg bg-white/5 py-1.5"><p className="text-[9px] uppercase text-emerald-200/50">Win Rate</p><p className="text-sm font-bold text-emerald-400">{botAcct.win_rate}%</p></div>
                  <div className="rounded-lg bg-white/5 py-1.5"><p className="text-[9px] uppercase text-emerald-200/50">Total Trades</p><p className="text-sm font-bold text-white">{botAcct.total_trades}</p></div>
                </div>
              )}
              <button onClick={botOpen} disabled={botBusy} data-testid="bot-trade-open-btn"
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-500 py-3 text-xs font-extrabold text-black shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-400 disabled:opacity-50">
                {botBusy ? <Loader2 className="animate-spin" size={16} /> : <InfinityIcon size={18} />}
                <span className="flex flex-col leading-tight"><span>TRADE OPEN</span><span className="text-[9px] font-semibold">(No Time Limit)</span></span>
              </button>
            </div>
          </div>

          {/* bot capital + tabs */}
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-white/5 p-2">
            <span className="text-[10px] font-bold uppercase text-emerald-200/50">Allocate Capital</span>
            <input type="number" value={alloc} onChange={(e) => setAlloc(e.target.value)} data-testid="bot-alloc-input"
              className="w-24 rounded border border-white/10 bg-black/30 px-2 py-1 text-xs text-white outline-none" />
            <button onClick={allocateBot} data-testid="bot-alloc-btn" className="rounded bg-emerald-500/20 px-2 py-1 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/30">Set</button>
            {botAcct && <span className="ml-auto flex gap-3 text-[10px] text-emerald-200/60">
              <span>Alloc <b className="text-white">${fmt(botAcct.allocated_capital)}</b></span>
              <span>Locked <b className="text-white">${fmt(botAcct.locked_capital)}</b></span>
              <span>Total P&L <b className={botAcct.total_pnl >= 0 ? "text-emerald-400" : "text-rose-400"}>${fmt(botAcct.total_pnl)}</b></span>
            </span>}
          </div>

          <div className="mt-3">
            <div className="flex gap-4 border-b border-white/10 text-[11px] font-bold">
              {[["active", `Active Bot Trades (${botPositions.length})`], ["history", "Bot Trade History"], ["perf", "Performance"]].map(([k, lbl]) => (
                <button key={k} onClick={() => setBotTab(k)} data-testid={`bot-tab-${k}`}
                  className={cn("pb-1.5", botTab === k ? "border-b-2 border-emerald-500 text-emerald-400" : "text-emerald-200/50")}>{lbl}</button>
              ))}
            </div>
            <div className="mt-2 max-h-40 overflow-auto">
              {botTab === "active" && (botPositions.length ? <PosTable rows={botPositions} onClose={closeBot} tp="bot" /> : <Empty text="No active bot trades — click TRADE OPEN" />)}
              {botTab === "history" && (botTrades.length ? <TradeTable rows={botTrades} /> : <Empty text="No bot trade history" />)}
              {botTab === "perf" && botAcct && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[["Win Rate", `${botAcct.win_rate}%`], ["Total Trades", botAcct.total_trades], ["Realized P&L", `$${fmt(botAcct.realized_pnl)}`], ["Unrealized", `$${fmt(botAcct.unrealized_pnl)}`], ["Open", botAcct.open_positions], ["Status", botAcct.status]].map(([l, v]) => (
                    <div key={l} className="rounded-lg bg-white/5 py-2"><p className="text-[9px] uppercase text-emerald-200/50">{l}</p><p className="text-sm font-bold text-white">{v}</p></div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Leaderboard */}
      <div className="neon-pulse rounded-2xl p-4" style={neon} data-testid="leaderboard-panel">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-base font-extrabold text-white"><Trophy size={18} className="text-amber-400" /> Top Self-Traders <span className="text-[11px] font-semibold text-emerald-200/50">· This Week</span></h2>
          {leaders?.me && <button onClick={() => setRankShare(leaders.me)} data-testid="share-rank-btn" className="flex items-center gap-1 rounded-lg bg-amber-400/15 px-3 py-1.5 text-[11px] font-bold text-amber-300 hover:bg-amber-400/25"><Share2 size={13} /> Share My Rank (#{leaders.me.rank})</button>}
        </div>
        {!leaders && <Empty text="Loading leaderboard…" />}
        {leaders && leaders.top.length === 0 && <Empty text="No self-trades closed this week yet — be the first!" />}
        {leaders && leaders.top.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[11px]">
              <thead className="text-emerald-200/50"><tr><th className="py-1">#</th><th>Trader</th><th>Profit</th><th>Trades</th><th>Win %</th></tr></thead>
              <tbody>
                {leaders.top.map((l) => (
                  <tr key={l.user_id} className={cn("border-t border-white/5", l.is_me && "bg-emerald-500/10")} data-testid={`leader-row-${l.rank}`}>
                    <td className="py-1.5 font-extrabold" style={{ color: l.rank === 1 ? "#f5c542" : l.rank === 2 ? "#cbd5e1" : l.rank === 3 ? "#e8a15a" : "#8fdcc0" }}>{l.rank}</td>
                    <td className="font-bold text-white">{l.name} {l.is_me && <span className="text-emerald-400">(You)</span>}</td>
                    <td className={cn("font-bold", l.profit >= 0 ? "text-emerald-400" : "text-rose-400")}>{l.profit >= 0 ? "+" : ""}${fmt(l.profit)}</td>
                    <td className="text-white">{l.trades}</td>
                    <td className="text-white">{l.win_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {leaders.me && leaders.me.rank > 10 && (
              <p className="mt-2 rounded-lg bg-emerald-500/10 px-2 py-1 text-[11px] font-bold text-emerald-300">Your rank: #{leaders.me.rank} · {leaders.me.profit >= 0 ? "+" : ""}${fmt(leaders.me.profit)} profit this week</p>
            )}
          </div>
        )}
      </div>

      {/* Footer feature strip */}
      <div className="grid grid-cols-3 gap-2 rounded-2xl px-4 py-3 sm:grid-cols-6" style={neon}>
        {FOOTER.map(([Ic, a, b]) => (
          <div key={a} className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-500/15 text-emerald-400"><Ic size={15} /></span>
            <span className="leading-tight"><span className="block text-[10px] font-bold text-white">{a}</span><span className="block text-[9px] text-emerald-200/50">{b}</span></span>
          </div>
        ))}
      </div>
      <TradeShareCard trade={shareTrade} user={user} onClose={() => setShareTrade(null)} />
      <RankShareCard data={rankShare} user={user} onClose={() => setRankShare(null)} />
      <FundWalletModal open={fundOpen} wallet={wallet} tab={fundTab} setTab={setFundTab} initialSource={wSource}
        onClose={() => setFundOpen(false)} onDeposit={(a) => depositWallet(a)}
        onChanged={() => { loadWallet(); loadAcct(); }} />
    </div>
  );
}

const WD_SOURCES = [
  { key: "capital", label: "Capital", desc: "Your self-trade deposit", full: true },
  { key: "profit", label: "Self-Trade Profit", desc: "72% to you · 28% to your team", full: false },
  { key: "commission", label: "Team Income", desc: "10-level trading commission", full: true },
];

function WithdrawView({ wallet, initialSource, onChanged }) {
  const [source, setSource] = React.useState(initialSource || "capital");
  const [amt, setAmt] = React.useState(5);
  const [addr, setAddr] = React.useState("");
  const [label, setLabel] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [sum, setSum] = React.useState(null);
  const load = React.useCallback(() => { api.get("/trading/withdraw/summary").then((r) => setSum(r.data)).catch(() => {}); }, []);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { setSource(initialSource || "capital"); }, [initialSource]);
  const bal = sum ? Number(sum[source] || 0) : 0;
  const feePct = sum ? Number(sum.fee_pct || 0) : 0;
  const preNet = source === "profit" ? Math.round(Number(amt) * 0.72 * 100) / 100 : Number(amt);
  const fee = Math.round(preNet * feePct / 100 * 100) / 100;
  const payout = Math.round((preNet - fee) * 100) / 100;
  const pendingFull = !!(sum && sum.max_pending > 0 && sum.pending >= sum.max_pending);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/trading/withdraw", { source, amount: Number(amt), wallet_address: addr.trim() });
      if (r.data.auto) toast.success(`Withdrawal auto-approved · $${fmt(r.data.payout)} sent to your BEP20 address`);
      else toast.success(`Request submitted · $${fmt(r.data.payout)} pending admin approval`);
      setAddr(""); load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Withdrawal failed"); } finally { setBusy(false); }
  };
  const saveAddr = async () => {
    if (!addr.trim()) return;
    try { await api.post("/trading/addresses", { address: addr.trim(), label: label.trim() }); toast.success("Address saved"); setLabel(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save address"); }
  };
  const delAddr = async (id) => { try { await api.delete(`/trading/addresses/${id}`); load(); } catch { toast.error("Delete failed"); } };
  const stColor = (s) => s === "paid" ? "bg-emerald-500/20 text-emerald-300" : s === "requested" ? "bg-amber-500/20 text-amber-300" : s === "rejected" || s === "failed" ? "bg-rose-500/20 text-rose-300" : "bg-white/10 text-white/60";
  const savedAddrs = sum?.addresses || [];
  const alreadySaved = savedAddrs.some((a) => a.address?.toLowerCase() === addr.trim().toLowerCase());
  return (
    <div data-testid="self-trade-withdraw-view">
      <div className="grid grid-cols-3 gap-1.5">
        {WD_SOURCES.map((s) => (
          <button key={s.key} onClick={() => setSource(s.key)} data-testid={`wd-source-${s.key}`}
            className={cn("rounded-lg border px-2 py-2 text-left transition", source === s.key ? "border-emerald-400 bg-emerald-500/15" : "border-white/10 bg-white/5 hover:border-white/20")}>
            <p className="text-[10px] font-bold text-white">{s.label}</p>
            <p className="text-[11px] font-extrabold text-emerald-300">${fmt(sum ? sum[s.key] : 0)}</p>
          </button>
        ))}
      </div>
      <p className="mt-2 text-[10px] text-emerald-200/50">{WD_SOURCES.find((x) => x.key === source)?.desc} · available <b className="text-white">${fmt(bal)}</b></p>
      {sum && sum.max_pending > 0 && (
        <p className={cn("mt-1 text-[10px]", pendingFull ? "text-rose-300" : "text-emerald-200/40")} data-testid="wd-pending-status">
          Pending requests: <b>{sum.pending}</b> / {sum.max_pending}{pendingFull ? " — limit reached, wait for approval" : ""}
        </p>
      )}
      <p className="mt-2 mb-1 text-[10px] font-bold uppercase text-emerald-200/50">Amount (USDT · min ${fmt(sum?.min ?? 5, 0)})</p>
      <input type="number" min={5} value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="wd-amount"
        className="mb-2 w-full rounded border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none" />
      {savedAddrs.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1" data-testid="wd-address-book">
          {savedAddrs.map((a) => (
            <span key={a.id} className={cn("group flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px]", addr.trim().toLowerCase() === a.address.toLowerCase() ? "border-emerald-400 bg-emerald-500/15 text-emerald-200" : "border-white/10 bg-white/5 text-white/70")}>
              <button onClick={() => setAddr(a.address)} data-testid={`wd-saved-${a.id}`} className="max-w-[140px] truncate">{a.label && a.label !== "Saved address" ? <b className="font-semibold">{a.label}</b> : null} <span className="font-mono opacity-80">{a.address.slice(0, 6)}…{a.address.slice(-4)}</span></button>
              <button onClick={() => delAddr(a.id)} data-testid={`wd-saved-del-${a.id}`} className="text-rose-300/70 hover:text-rose-300"><X size={9} /></button>
            </span>
          ))}
        </div>
      )}
      <p className="mb-1 text-[10px] font-bold uppercase text-emerald-200/50">Your BEP20 USDT Address</p>
      <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (optional — e.g. Trust Wallet, Binance)" data-testid="wd-address-label"
        className="mb-1.5 w-full rounded border border-white/10 bg-black/30 px-3 py-1.5 text-[11px] text-white outline-none" />
      <div className="mb-2 flex flex-col gap-2 sm:flex-row">
        <input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="0x… (BNB Smart Chain / BEP20)" data-testid="wd-address"
          className="w-full rounded border border-white/10 bg-black/30 px-3 py-2 text-[11px] font-mono text-white outline-none" />
        <button onClick={saveAddr} disabled={!addr.trim() || alreadySaved} data-testid="wd-save-address" title="Save to address book"
          className="shrink-0 rounded border border-emerald-500/40 px-3 py-2 text-[10px] font-bold text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-30 sm:py-0">{alreadySaved ? "Saved" : "Save"}</button>
      </div>
      {source === "profit" && (
        <p className="mb-2 rounded-lg bg-amber-400/10 px-2 py-1.5 text-[10px] text-amber-300">You receive <b>${fmt(payout)}</b> on-chain (72%{feePct > 0 ? ` − ${fmt(feePct)}% fee` : ""}); <b>${fmt(Math.round(Number(amt) * 0.28 * 100) / 100)}</b> (28%) is shared across your 10 upline levels.</p>
      )}
      {source !== "profit" && feePct > 0 && (
        <p className="mb-2 rounded-lg bg-white/5 px-2 py-1.5 text-[10px] text-emerald-200/60">Withdrawal fee <b>{fmt(feePct)}%</b> = <b>${fmt(fee)}</b> · you receive <b className="text-white">${fmt(payout)}</b> on-chain.</p>
      )}
      <button onClick={submit} disabled={busy || Number(amt) < (sum?.min ?? 5) || !addr.trim() || Number(amt) > bal || pendingFull} data-testid="wd-submit-btn"
        className="w-full rounded-lg bg-emerald-500 py-2.5 text-sm font-extrabold text-black hover:bg-emerald-400 disabled:opacity-40">
        {busy ? "Submitting…" : pendingFull ? "Pending limit reached" : `Withdraw $${fmt(payout)} to BEP20`}
      </button>
      <p className="mt-1 text-[9px] text-emerald-200/40">Payout goes directly to your BEP20 address after admin approval (or instantly if auto-approve is on). Self-trade wallets never mix with your MLM balance.</p>
      {sum?.history?.length > 0 && (
        <div className="mt-3 border-t border-white/10 pt-2" data-testid="wd-history">
          <p className="mb-1 text-[10px] font-bold uppercase text-emerald-200/50">Withdrawal History</p>
          <div className="max-h-40 space-y-1 overflow-y-auto">
            {sum.history.map((h) => (
              <div key={h.wd_id} data-testid={`wd-row-${h.wd_id}`} className="flex items-center justify-between gap-2 rounded bg-white/5 px-2 py-1.5 text-[9px]">
                <span className="shrink-0 rounded bg-white/10 px-1 py-0.5 font-bold uppercase text-white/70">{h.source}</span>
                {h.tx_hash ? (
                  <a href={`https://bscscan.com/tx/${h.tx_hash}`} target="_blank" rel="noreferrer" title={h.tx_hash}
                    className="truncate font-mono text-emerald-300 underline hover:text-emerald-200" data-testid={`wd-hash-${h.wd_id}`}>{h.tx_hash.slice(0, 10)}…{h.tx_hash.slice(-6)}</a>
                ) : <span className="truncate font-mono text-white/40">{h.wallet_address.slice(0, 8)}…</span>}
                <span className="shrink-0 text-white/70">${fmt(h.payout ?? h.net)}</span>
                <span className={cn("shrink-0 rounded px-1.5 py-0.5 font-bold uppercase", stColor(h.status))}>{h.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FundWalletModal({ open, wallet, tab, setTab, initialSource, onClose, onDeposit, onChanged }) {
  const [amt, setAmt] = React.useState(100);
  const [cfg, setCfg] = React.useState(null);
  const [tx, setTx] = React.useState("");
  const [qr, setQr] = React.useState("");
  const [deposits, setDeposits] = React.useState([]);
  const loadHistory = React.useCallback(() => { api.get("/trading/deposit/history").then((r) => setDeposits(r.data.deposits || [])).catch(() => {}); }, []);
  React.useEffect(() => {
    if (!open) return;
    api.get("/trading/deposit/config").then((r) => {
      setCfg(r.data);
      if (r.data?.address) QRCode.toDataURL(r.data.address, { margin: 1, width: 160 }).then(setQr).catch(() => {});
    }).catch(() => {});
    loadHistory();
  }, [open, loadHistory]);
  const verifyDeposit = async () => {
    try {
      const r = await api.post("/trading/deposit", { tx_hash: tx.trim() });
      const st = r.data.deposit.status;
      if (st === "CONFIRMED") toast.success("On-chain deposit credited to Trading Capital");
      else toast.info(`Deposit ${st} — ${r.data.deposit.reason || "awaiting confirmations"}. Re-check shortly.`);
      setTx(""); loadHistory(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
  };
  if (!open) return null;
  const QUICK = [50, 100, 250, 500, 1000];
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center overflow-y-auto bg-black/80 p-4" data-testid="fund-wallet-modal">
      <div className="w-full max-w-md rounded-2xl p-5" style={{ background: "linear-gradient(160deg,#0f1613,#0a0f0c)", border: "1px solid rgba(16,185,129,.35)" }}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-extrabold text-white"><Wallet size={18} className="text-emerald-400" /> Self-Trade Wallet</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="fund-close"><X size={18} /></button>
        </div>
        <div className="mb-3 grid grid-cols-2 gap-1.5 rounded-lg bg-black/30 p-1 text-center text-[11px] font-bold">
          <button onClick={() => setTab("deposit")} data-testid="fund-tab-deposit" className={cn("rounded-md py-1.5", tab === "deposit" ? "bg-emerald-500 text-black" : "text-emerald-200/60")}>Deposit</button>
          <button onClick={() => setTab("withdraw")} data-testid="fund-tab-withdraw" className={cn("rounded-md py-1.5", tab === "withdraw" ? "bg-emerald-500 text-black" : "text-emerald-200/60")}>Withdraw</button>
        </div>

        {tab === "withdraw" ? (
          <WithdrawView wallet={wallet} initialSource={initialSource} onChanged={onChanged} />
        ) : (
        <div>
        <div className="mb-3 grid grid-cols-2 gap-2 text-center">
          <div className="rounded-lg bg-white/5 py-2"><p className="text-[9px] uppercase text-emerald-200/50">Trading Capital</p><p className="text-sm font-bold text-white">${fmt(wallet?.balance || 0)}</p></div>
          <div className="rounded-lg bg-white/5 py-2"><p className="text-[9px] uppercase text-emerald-200/50">Main Fund</p><p className="text-sm font-bold text-white">${fmt(wallet?.mlm_fund_balance || 0)}</p></div>
        </div>
        <p className="mb-1 text-[10px] font-bold uppercase text-emerald-200/50">Transfer from Main Fund (USDT)</p>
        <div className="mb-2 grid grid-cols-5 gap-1">
          {QUICK.map((a) => (<button key={a} onClick={() => setAmt(a)} data-testid={`fund-quick-${a}`} className={cn("rounded py-1 text-[11px] font-bold", Number(amt) === a ? "bg-emerald-500 text-black" : "bg-white/5 text-emerald-200/60")}>${a}</button>))}
        </div>
        <input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="fund-amount"
          className="mb-3 w-full rounded border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none" />
        <button onClick={() => onDeposit(amt)} data-testid="fund-deposit-btn" className="flex w-full items-center justify-center gap-1 rounded-lg bg-emerald-500 py-2.5 text-sm font-extrabold text-black hover:bg-emerald-400"><ArrowRightLeft size={14} /> Transfer In</button>
        {cfg?.address && (
          <div className="mt-3 rounded-lg border border-emerald-500/25 bg-black/20 p-3" data-testid="onchain-deposit-section">
            <p className="mb-2 text-[11px] font-bold text-emerald-300">Or deposit on-chain · direct to Trading Capital (USDT · BEP20)</p>
            {qr && <div className="mb-2 flex justify-center"><img src={qr} alt="Deposit address QR" width={140} height={140} className="rounded-lg bg-white p-1.5" data-testid="onchain-qr" /></div>}
            <div className="flex items-center gap-2">
              <input readOnly value={cfg.address} className="w-full truncate rounded bg-black/30 px-2 py-1.5 text-[10px] text-white/80 outline-none" data-testid="onchain-address" />
              <button onClick={() => { navigator.clipboard.writeText(cfg.address); toast.success("Address copied"); }} data-testid="onchain-copy-btn" className="flex items-center gap-1 rounded bg-emerald-500/20 px-2 py-1.5 text-[10px] font-bold text-emerald-300 hover:bg-emerald-500/30"><Copy size={11} />Copy</button>
            </div>
            <p className="mt-1.5 text-[9px] text-emerald-200/40">Scan the QR or send only USDT via BEP20 to this address (min ${fmt(cfg.min)}), then paste your transaction hash to auto-confirm:</p>
            <div className="mt-1 flex gap-2">
              <input value={tx} onChange={(e) => setTx(e.target.value)} placeholder="0x transaction hash" data-testid="onchain-tx" className="w-full rounded border border-white/10 bg-black/30 px-2 py-1.5 text-[10px] text-white outline-none" />
              <button onClick={verifyDeposit} data-testid="onchain-verify-btn" className="rounded bg-amber-400 px-3 py-1.5 text-[10px] font-bold text-black hover:bg-amber-300">Verify</button>
            </div>
            {deposits.length > 0 && (
              <div className="mt-3 border-t border-white/10 pt-2" data-testid="onchain-history">
                <p className="mb-1 text-[10px] font-bold uppercase text-emerald-200/50">Deposit History</p>
                <div className="max-h-40 space-y-1 overflow-y-auto">
                  {deposits.map((d) => (
                    <div key={d.id} data-testid={`deposit-row-${d.id}`} className="flex items-center justify-between gap-2 rounded bg-white/5 px-2 py-1.5 text-[9px]">
                      <a href={`https://bscscan.com/tx/${d.tx_hash}`} target="_blank" rel="noreferrer" title={d.tx_hash}
                        className="truncate font-mono text-emerald-300 underline hover:text-emerald-200" data-testid={`deposit-hash-${d.id}`}>
                        {d.tx_hash.slice(0, 10)}…{d.tx_hash.slice(-8)}
                      </a>
                      <span className="shrink-0 text-white/60">{d.amount ? `$${fmt(d.amount)}` : "—"}</span>
                      <span className={cn("shrink-0 rounded px-1.5 py-0.5 font-bold",
                        d.status === "CONFIRMED" ? "bg-emerald-500/20 text-emerald-300" :
                        d.status === "PENDING" ? "bg-amber-500/20 text-amber-300" : "bg-rose-500/20 text-rose-300")}>
                        {d.status}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-1 text-[8px] text-emerald-200/30">Tap a hash to view the transaction on BscScan.</p>
              </div>
            )}
          </div>
        )}
        <p className="mt-2 text-[10px] text-emerald-200/40">Transfer moves USDT from your Main (fund) Wallet into your isolated Trading Capital. Withdrawals go directly to your own BEP20 address from the Withdraw tab.</p>
        </div>
        )}
      </div>
    </div>
  );
}

function Empty({ text }) { return <p className="py-4 text-center text-[11px] text-emerald-200/40">{text}</p>; }

function PosTable({ rows, onClose, tp }) {
  return (
    <table className="w-full text-left text-[10px]">
      <thead className="text-emerald-200/50"><tr><th className="py-1">Symbol</th><th>Type</th><th>Amount</th><th>Entry</th><th>Current</th><th>P&L</th><th>Status</th><th>Action</th></tr></thead>
      <tbody>
        {rows.map((p) => (
          <tr key={p.id} className="border-t border-white/5" data-testid={`${tp}-pos-${p.id}`}>
            <td className="py-1.5 font-bold text-white">{p.symbol}</td>
            <td className={p.side === "BUY" ? "text-emerald-400" : "text-rose-400"}>{p.side}</td>
            <td className="text-white">${fmt(p.amount)}</td>
            <td className="text-white">{fmt(p.entry, 2)}</td>
            <td className="text-white">{fmt(p.current, 2)}</td>
            <td className={cn("font-bold", p.pnl >= 0 ? "text-emerald-400" : "text-rose-400")}>{p.pnl >= 0 ? "+" : ""}${fmt(p.pnl)}</td>
            <td className="text-emerald-400">OPEN</td>
            <td><button onClick={() => onClose(p.id)} data-testid={`${tp}-close-${p.id}`} className="flex items-center gap-0.5 rounded bg-rose-500/20 px-1.5 py-0.5 font-bold text-rose-400 hover:bg-rose-500/30"><X size={10} />Close</button></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TradeTable({ rows, onShare }) {
  return (
    <table className="w-full text-left text-[10px]">
      <thead className="text-emerald-200/50"><tr><th className="py-1">Symbol</th><th>Type</th><th>Amount</th><th>Entry</th><th>Exit</th><th>Net P&L</th><th>Closed</th>{onShare && <th></th>}</tr></thead>
      <tbody>
        {rows.map((t) => (
          <tr key={t.id} className="border-t border-white/5">
            <td className="py-1.5 font-bold text-white">{t.symbol}</td>
            <td className={t.side === "BUY" ? "text-emerald-400" : "text-rose-400"}>{t.side}</td>
            <td className="text-white">${fmt(t.amount)}</td>
            <td className="text-white">{fmt(t.entry, 2)}</td>
            <td className="text-white">{fmt(t.exit, 2)}</td>
            <td className={cn("font-bold", t.net_pnl >= 0 ? "text-emerald-400" : "text-rose-400")}>{t.net_pnl >= 0 ? "+" : ""}${fmt(t.net_pnl)}</td>
            <td className="text-emerald-200/50">{t.closed_at?.slice(5, 16).replace("T", " ")}</td>
            {onShare && <td><button onClick={() => onShare(t)} data-testid={`share-trade-${t.id}`} className="flex items-center gap-0.5 rounded bg-emerald-500/20 px-1.5 py-0.5 font-bold text-emerald-300 hover:bg-emerald-500/30"><Share2 size={10} />Share</button></td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
